import type {
  AppLocale,
  CompanyProfileRecord,
  ContractGenerationContextInput,
  ContractTemplateCategory,
  ContractTemplateCollectionRecord,
  ContractTemplateDetailRecord,
  ContractTemplatePlaceholderRecord,
  ContractTemplateRecord,
  ContractTemplateStatus,
  ContractTemplateUpsertInput
} from '@shared/types';

export interface ContractTemplateRowViewModel {
  id: string;
  name: string;
  categoryLabel: string;
  languageLabel: string;
  issuerName: string;
  versionLabel: string;
  statusLabel: string;
  statusTone: 'neutral' | 'primary' | 'warning' | 'success';
  updatedLabel: string;
}

export interface ContractTemplateSelectionItemViewModel {
  id: string;
  label: string;
  subtitle: string | null;
}

export type ContractTemplateSort = 'updated' | 'name' | 'version';

function getCurrentVersion(
  collection: Pick<ContractTemplateCollectionRecord, 'templates' | 'versions'>,
  template: ContractTemplateRecord
) {
  return (
    collection.versions.find((version) => version.id === template.currentVersionId) ??
    collection.versions.find((version) => version.templateId === template.id) ??
    null
  );
}

function templateStatusTone(status: ContractTemplateStatus): ContractTemplateRowViewModel['statusTone'] {
  switch (status) {
    case 'active':
      return 'success';
    case 'archived':
      return 'neutral';
    default:
      return 'warning';
  }
}

function formatShortDate(locale: string, value: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value));
}

function getLanguageLabel(language: AppLocale, t: (key: string) => string): string {
  return t(`common.languages.${language}`);
}

function getIssuerLabel(
  companyProfiles: CompanyProfileRecord[],
  issuerCompanyProfileId: string | null,
  fallback: string
): string {
  if (!issuerCompanyProfileId) {
    return fallback;
  }

  return companyProfiles.find((profile) => profile.id === issuerCompanyProfileId)?.name ?? fallback;
}

function sortTemplates(
  collection: ContractTemplateCollectionRecord,
  sort: ContractTemplateSort
): ContractTemplateRecord[] {
  const templates = [...collection.templates];

  switch (sort) {
    case 'name':
      return templates.sort((left, right) => left.name.localeCompare(right.name));
    case 'version':
      return templates.sort((left, right) => {
        const leftVersion = getCurrentVersion(collection, left)?.versionNumber ?? 0;
        const rightVersion = getCurrentVersion(collection, right)?.versionNumber ?? 0;
        return rightVersion - leftVersion;
      });
    default:
      return templates.sort((left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());
  }
}

export function selectContractTemplatesSummary(collection: ContractTemplateCollectionRecord) {
  return {
    activeCount: collection.templates.filter((template) => template.status === 'active').length,
    draftCount: collection.templates.filter((template) => template.status === 'draft').length,
    archivedCount: collection.templates.filter((template) => template.status === 'archived').length
  };
}

export function selectContractTemplateRows(
  collection: ContractTemplateCollectionRecord,
  options: {
    locale: string;
    statusFilter: 'all' | ContractTemplateStatus;
    categoryFilter: 'all' | ContractTemplateCategory;
    languageFilter: 'all' | AppLocale;
    sort: ContractTemplateSort;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): ContractTemplateRowViewModel[] {
  return sortTemplates(collection, options.sort)
    .filter((template) => {
      const matchesStatus = options.statusFilter === 'all' || template.status === options.statusFilter;
      const matchesCategory = options.categoryFilter === 'all' || template.category === options.categoryFilter;
      const matchesLanguage = options.languageFilter === 'all' || template.language === options.languageFilter;
      return matchesStatus && matchesCategory && matchesLanguage;
    })
    .map((template) => {
      const currentVersion = getCurrentVersion(collection, template);

      return {
        id: template.id,
        name: template.name,
        categoryLabel: options.t(`enums.contractTemplateCategory.${template.category}`),
        languageLabel: getLanguageLabel(template.language, options.t),
        issuerName: getIssuerLabel(collection.companyProfiles, template.defaultIssuerCompanyProfileId, options.t('common.notSet')),
        versionLabel: currentVersion
          ? options.t('contracts.templates.versions.versionLabel', { version: currentVersion.versionNumber })
          : options.t('contracts.templates.versions.none'),
        statusLabel: options.t(`enums.contractTemplateStatus.${template.status}`),
        statusTone: templateStatusTone(template.status),
        updatedLabel: formatShortDate(options.locale, template.updatedAt)
      };
    });
}

export function selectContractTemplateSelectionItems(
  collection: ContractTemplateCollectionRecord
): ContractTemplateSelectionItemViewModel[] {
  return collection.templates.map((template) => ({
    id: template.id,
    label: template.name,
    subtitle: template.slug
  }));
}

export function createEmptyContractTemplateDraft(
  companyProfiles: CompanyProfileRecord[]
): ContractTemplateUpsertInput {
  return {
    slug: '',
    name: '',
    category: 'website_development',
    language: 'ro-RO',
    status: 'draft',
    notes: null,
    defaultIssuerCompanyProfileId: companyProfiles.find((profile) => profile.isDefault)?.id ?? companyProfiles[0]?.id ?? null,
    version: {
      bodySource: '',
      placeholderSchema: [],
      outputRules: {
        numberingPrefix: null,
        signatureFooter: null
      },
      changeSummary: null
    }
  };
}

export function createContractTemplateDraft(detail: ContractTemplateDetailRecord): ContractTemplateUpsertInput {
  return {
    id: detail.template.id,
    slug: detail.template.slug,
    name: detail.template.name,
    category: detail.template.category,
    language: detail.template.language,
    status: detail.template.status,
    notes: detail.template.notes,
    defaultIssuerCompanyProfileId: detail.template.defaultIssuerCompanyProfileId,
    version: {
      bodySource: detail.currentVersion?.bodySource ?? '',
      placeholderSchema: detail.currentVersion?.placeholderSchema ?? [],
      outputRules: detail.currentVersion?.outputRules ?? {
        numberingPrefix: null,
        signatureFooter: null
      },
      changeSummary: null
    }
  };
}

export function createEmptyPlaceholder(): ContractTemplatePlaceholderRecord {
  return {
    key: '',
    group: 'manual',
    labelKey: '',
    valueType: 'string',
    required: false,
    sourceKind: 'manual',
    fallbackNote: null
  };
}

export function createInitialGenerationContext(
  detail: ContractTemplateDetailRecord
): ContractGenerationContextInput {
  return {
    customerId: detail.customerSummaries[0]?.id ?? '',
    projectId: detail.projectSummaries.find((project) => project.customerId === detail.customerSummaries[0]?.id)?.id ?? null,
    issuerCompanyProfileId:
      detail.template.defaultIssuerCompanyProfileId ??
      detail.generationDefaults.defaultIssuerCompanyProfileId ??
      detail.companyProfiles.find((profile) => profile.isDefault)?.id ??
      detail.companyProfiles[0]?.id ??
      null,
    contractValues: {
      title: detail.template.name,
      effectiveFrom: null,
      effectiveUntil: null,
      totalValue: null,
      currency: detail.generationDefaults.defaultCurrency,
      serviceScopeSummary: null,
      paymentNotes: null,
      scheduleSummary: null,
      renewalNotes: null,
      terminationNotes: null
    },
    manualTokenValues: {}
  };
}

export function getPlaceholderLabel(
  placeholder: ContractTemplatePlaceholderRecord,
  t: (key: string) => string
): string {
  const translated = t(placeholder.labelKey);
  return translated === placeholder.labelKey ? placeholder.key : translated;
}

export function selectManualPlaceholders(placeholders: ContractTemplatePlaceholderRecord[]): ContractTemplatePlaceholderRecord[] {
  return placeholders.filter((placeholder) => placeholder.sourceKind === 'manual');
}

export function isTemplateDraftDirty(
  draft: ContractTemplateUpsertInput,
  detail: ContractTemplateDetailRecord | null
): boolean {
  if (!detail) {
    return Boolean(draft.name || draft.slug || draft.version.bodySource || draft.version.placeholderSchema.length > 0);
  }

  const currentDraft = createContractTemplateDraft(detail);

  return JSON.stringify(draft) !== JSON.stringify(currentDraft);
}
